import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import { addSubcontractor, getSubcontractors } from "../../../components/clients/clientsSlice";
import { uploadAsset } from "../../../assets/assetsSlice";
import { SelectInput } from "../../../components/inputs/Select";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "14px";

const fieldLabelSx = {
    fontWeight: 700,
    mb: 0.75,
    letterSpacing: "0.04em",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.72rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 0,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.10)",
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const errorTextFieldSx = {
    ...glassmorphicTextFieldSx,
    "& .MuiOutlinedInput-root": {
        ...glassmorphicTextFieldSx["& .MuiOutlinedInput-root"],
        "& fieldset": {
            borderColor: "#d32f2f",
        },
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(20px) saturate(1.3)",
    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const sectionTitleSx = {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: "text.primary",
    mb: 2,
    letterSpacing: "-0.01em",
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const INITIAL_FORM = {
    name: "",
    email: "",
    address: "",
    reminders_dates: "",
    reminders_comments: "",
    date_attestation_urssaf: null,
    date_fin_attestation_urssaf: null,
    date_attestation_fiscale: null,
    date_fin_attestation_fiscale: null,
    date_fin_validite_rc: null,
    date_fin_attestation_salarie_etranger: null,
    status: "Actif",
    type: "Indépendant",
};

export default function MobileAddSubcontractorModal(props) {
    const dispatch = useDispatch();

    const typeOptions = useSelector((state) => state.clients.providerTypes);

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [inputStyle, setInputStyle] = useState(false);
    const [files, setFiles] = useState([]);

    const statusOptions = [
        { label: "Actif", value: "Actif" },
        { label: "Inactif", value: "Inactif" },
    ];

    const handleFieldChange = (field, e) => {
        setForm((prev) => ({ ...prev, [field]: e }));
    };

    const handleFileUpload = (e) => {
        setFiles(e.target.files);
    };

    const handleAddSubcontractor = () => {
        if (form.name.trim() === "" || form.email.trim() === "") {
            showErrorToast('Les champs "nom" et "email" sont obligatoires');
            setInputStyle(true);
            return;
        }
        let payload = { ...form };
        payload = {
            ...payload,
            date_attestation_urssaf: form.date_attestation_urssaf
                ? moment(form.date_attestation_urssaf).format("YYYY-MM-DD")
                : null,
            date_fin_attestation_urssaf: form.date_fin_attestation_urssaf
                ? moment(form.date_fin_attestation_urssaf).format("YYYY-MM-DD")
                : null,
            date_attestation_fiscale: form.date_attestation_fiscale
                ? moment(form.date_attestation_fiscale).format("YYYY-MM-DD")
                : null,
            date_fin_attestation_fiscale: form.date_fin_attestation_fiscale
                ? moment(form.date_fin_attestation_fiscale).format("YYYY-MM-DD")
                : null,
            date_fin_validite_rc: form.date_fin_validite_rc
                ? moment(form.date_fin_validite_rc).format("YYYY-MM-DD")
                : null,
            date_fin_attestation_salarie_etranger: form.date_fin_attestation_salarie_etranger
                ? moment(form.date_fin_attestation_salarie_etranger).format("YYYY-MM-DD")
                : null,
        };
        dispatch(addSubcontractor({ subcontractor: payload }))
            .then((response) => {
                const responseData = response.payload.responseData;
                if (responseData && responseData.status === 1) {
                    const newSubcontractor = { ...form, id: responseData.insert_id, assets: [] };
                    if (files.length > 0) {
                        Array.from(files).forEach((file) => {
                            dispatch(
                                uploadAsset({
                                    tableName: "subcontractors",
                                    idParent: responseData.insert_id,
                                    filename: file.name,
                                    file,
                                })
                            );
                        });
                    }
                    showSuccessToast("Subcontractor added successfully");
                    handleClose(true);
                }
            })
            .catch((error) => {
                showErrorToast("Error adding subcontractor");
                console.error("Error:", error);
            });
    };

    const handleClose = (check) => {
        props.onHide();
        setInputStyle(false);
        setForm({ ...INITIAL_FORM });
        setFiles([]);
        if (check) {
            dispatch(getSubcontractors());
        }
    };

    /* Helper: format date for native input */
    const formatDateForInput = (date) => {
        if (!date) return "";
        return moment(date).format("YYYY-MM-DD");
    };

    /* Helper: parse native date input */
    const parseDateInput = (value) => {
        if (!value) return null;
        return new Date(value + "T00:00:00");
    };

    return (
        <Dialog
            fullScreen
            open={props.visible}
            onClose={() => handleClose()}
            TransitionComponent={Transition}
            sx={{ zIndex: 1400 }}
            PaperProps={{
                sx: {
                    background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
                },
            }}
        >
            {/* ── Sticky AppBar ── */}
            <AppBar
                sx={{
                    position: "sticky",
                    top: 0,
                    background: "rgba(255,255,255,0.82)",
                    backdropFilter: "blur(24px) saturate(1.5)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                    boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    color: "text.primary",
                    zIndex: 10,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                    },
                }}
            >
                <Toolbar sx={{ minHeight: 56, px: 1 }}>
                    <IconButton
                        edge="start"
                        onClick={() => handleClose()}
                        sx={{
                            mr: 1,
                            color: "text.secondary",
                            "&:active": { transform: "scale(0.92)" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        <Langs str="ADD_SUBCONTRACTOR" />
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* ── Body ── */}
            <Box sx={{ px: 2, pt: 2.5, pb: 14, mt: 0 }}>
                {/* ── General Info ── */}
                <Box sx={glassCardSx}>
                    <Typography sx={sectionTitleSx}>Informations générales</Typography>

                    {/* Name */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="Nom" />
                    </Typography>
                    <TextField
                        value={form.name}
                        onChange={(e) => handleFieldChange("name", e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputStyle && !form.name ? errorTextFieldSx : glassmorphicTextFieldSx}
                        placeholder="Nom"
                    />
                    <Box sx={{ mb: 2 }} />

                    {/* Email */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="EMAIL" />
                    </Typography>
                    <TextField
                        value={form.email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        fullWidth
                        size="small"
                        type="email"
                        sx={inputStyle && !form.email ? errorTextFieldSx : glassmorphicTextFieldSx}
                        placeholder="Email"
                        inputProps={{ inputMode: "email" }}
                    />
                    <Box sx={{ mb: 2 }} />

                    {/* Address */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="ADDRESS" />
                    </Typography>
                    <TextField
                        value={form.address}
                        onChange={(e) => handleFieldChange("address", e.target.value)}
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        placeholder="Adresse"
                    />
                    <Box sx={{ mb: 2 }} />

                    {/* Reminders Dates */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="REMINDERS_DATES" />
                    </Typography>
                    <TextField
                        value={form.reminders_dates}
                        onChange={(e) => handleFieldChange("reminders_dates", e.target.value)}
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        placeholder="Dates de rappel"
                    />
                </Box>

                {/* ── Status & Type ── */}
                <Box sx={glassCardSx}>
                    <Typography sx={sectionTitleSx}>Statut et type</Typography>

                    <Typography sx={fieldLabelSx}>
                        <Langs str="STATUS" />
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <SelectInput
                            options={statusOptions}
                            value={form.status}
                            onChange={(e) => handleFieldChange("status", e.value)}
                        />
                    </Box>

                    <Typography sx={fieldLabelSx}>
                        <Langs str="TYPE" />
                    </Typography>
                    <Box sx={{ mb: 0 }}>
                        <SelectInput
                            options={typeOptions}
                            value={form.type}
                            onChange={(e) => handleFieldChange("type", e.value)}
                        />
                    </Box>
                </Box>

                {/* ── Attestation URSSAF ── */}
                <Box sx={glassCardSx}>
                    <Typography sx={sectionTitleSx}>Attestation URSSAF</Typography>

                    <Typography sx={fieldLabelSx}>
                        <Langs str="DATE_ATTESTATION_URSSAF" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(form.date_attestation_urssaf)}
                        onChange={(e) =>
                            handleFieldChange("date_attestation_urssaf", parseDateInput(e.target.value))
                        }
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={{ mb: 2 }} />

                    <Typography sx={fieldLabelSx}>
                        <Langs str="DATE_FIN_ATTESTATION_URSSAF" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(form.date_fin_attestation_urssaf)}
                        onChange={(e) =>
                            handleFieldChange(
                                "date_fin_attestation_urssaf",
                                parseDateInput(e.target.value)
                            )
                        }
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* ── Attestation Fiscale ── */}
                <Box sx={glassCardSx}>
                    <Typography sx={sectionTitleSx}>Attestation fiscale</Typography>

                    <Typography sx={fieldLabelSx}>
                        <Langs str="DATE_ATTESTATION_FISCALE" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(form.date_attestation_fiscale)}
                        onChange={(e) =>
                            handleFieldChange(
                                "date_attestation_fiscale",
                                parseDateInput(e.target.value)
                            )
                        }
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={{ mb: 2 }} />

                    <Typography sx={fieldLabelSx}>
                        <Langs str="DATE_FIN_ATTESTATION_FISCALE" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(form.date_fin_attestation_fiscale)}
                        onChange={(e) =>
                            handleFieldChange(
                                "date_fin_attestation_fiscale",
                                parseDateInput(e.target.value)
                            )
                        }
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* ── Other dates ── */}
                <Box sx={glassCardSx}>
                    <Typography sx={sectionTitleSx}>Autres dates</Typography>

                    <Typography sx={fieldLabelSx}>
                        <Langs str="DATE_FIN_VALIDITE_RC" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(form.date_fin_validite_rc)}
                        onChange={(e) =>
                            handleFieldChange("date_fin_validite_rc", parseDateInput(e.target.value))
                        }
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={{ mb: 2 }} />

                    <Typography sx={fieldLabelSx}>
                        <Langs str="DATE_FIN_ATTESTATION_SALARIE_ETRANGER" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(form.date_fin_attestation_salarie_etranger)}
                        onChange={(e) =>
                            handleFieldChange(
                                "date_fin_attestation_salarie_etranger",
                                parseDateInput(e.target.value)
                            )
                        }
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* ── File Upload ── */}
                <Box sx={glassCardSx}>
                    <Typography sx={sectionTitleSx}>
                        <Langs str="UPLOAD_FILES" />
                    </Typography>

                    <Button
                        component="label"
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                            minHeight: 52,
                            borderRadius: "14px",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            textTransform: "none",
                            borderColor: "rgba(0,0,0,0.12)",
                            color: "text.secondary",
                            "&:hover": {
                                borderColor: "rgba(255,144,21,0.5)",
                                backgroundColor: "rgba(255,144,21,0.04)",
                            },
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                        }}
                    >
                        {Localize("CHOOSE_FILES")}
                        <input
                            type="file"
                            multiple
                            hidden
                            onChange={handleFileUpload}
                        />
                    </Button>

                    {files.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            {Array.from(files).map((file, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        py: 0.75,
                                        px: 1,
                                        borderRadius: "8px",
                                        backgroundColor: "rgba(255,144,21,0.06)",
                                        mb: 0.5,
                                    }}
                                >
                                    <AttachFileIcon
                                        sx={{ fontSize: 18, color: "#FF9015" }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: "0.85rem",
                                            color: "text.secondary",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {file.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* ── Sticky Bottom Button ── */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: 2,
                    py: 2,
                    background: "rgba(255,255,255,0.88)",
                    backdropFilter: "blur(24px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                    borderTop: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                    zIndex: 10,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddSubcontractor}
                    sx={{
                        minHeight: 52,
                        borderRadius: "14px",
                        fontWeight: 700,
                        fontSize: "1rem",
                        textTransform: "none",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                        boxShadow: "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)",
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                    }}
                >
                    <Langs str="ADD" />
                </Button>
            </Box>
        </Dialog>
    );
}
